import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DefaultTheme, StyledComponent } from 'styled-components';
import { Icon } from '../Icon';
import { Transition } from '../Transition';
import * as Styled from './Dialog.styled';

type DialogProps = Styled.BaseProps & {
  show?: boolean;
  hideBackdrop?: boolean;
  hideCloseButton?: boolean;
  preventBackdropClose?: boolean;
  preventEscapeClose?: boolean;
  onClose?: () => void;
  onCloseEnd?: (e: TransitionEvent) => void;
  styledContent?: StyledComponent<'div', DefaultTheme>;
  styledCloseButton?: StyledComponent<'div', DefaultTheme>;
};

export const Dialog = ({
  children,
  show,
  hideBackdrop,
  hideCloseButton,
  preventBackdropClose,
  preventEscapeClose,
  onClose,
  onCloseEnd,
  styledContent,
  styledCloseButton,
  ...props
}: PropsWithChildren<DialogProps>) => {
  const handleBackdropClose = () => {
    if (!preventBackdropClose) {
      onClose?.();
    }
  };

  const handleEscapeClose = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventEscapeClose) {
        onClose?.();
      }
    },
    [onClose, preventEscapeClose],
  );

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleEscapeClose, false);
      document.body.style.setProperty('overflow', 'hidden');
      return () => {
        document.removeEventListener('keydown', handleEscapeClose, false);
        document.body.style.removeProperty('overflow');
      };
    }
  }, [handleEscapeClose, show]);

  return createPortal(
    <Transition as={Styled.Base} visible={show} onTransitionEnd={onCloseEnd} {...props}>
      {!hideBackdrop && <Styled.Backdrop onClick={handleBackdropClose} />}
      <Styled.ContentBox as={styledContent}>
        {!hideCloseButton && (
          <Styled.CloseButton as={styledCloseButton} onClick={onClose}>
            <Icon icon="close" size="1x" />
          </Styled.CloseButton>
        )}
        {children}
      </Styled.ContentBox>
    </Transition>,
    document.body,
  );
};

Dialog.Title = Styled.Title;
Dialog.Actions = Styled.Actions;
Dialog.Body = Styled.Body;
